import React, { Component, createRef } from "react";
import * as s from "./UserRegisterModal.module.scss";
import {
  Button,
  Checkbox,
  Input,
  Modal,
  P,
  PseudoLink,
} from "src/views/shared/components";
import { Icons } from "src/libs/assets";
import { inject, observer } from "mobx-react";
import { LoginStore, RegisterStore } from "src/stores";
import ReCAPTCHA from "react-google-recaptcha";
import { tx, txWithComponent } from "src/libs/i18n";
import { ISettings } from "src/models";
import { graphql, StaticQuery } from "gatsby";

interface Props {
  registerStore?: RegisterStore;
  loginStore?: LoginStore;
  settings?: ISettings;
}

@inject("registerStore", "loginStore")
@observer
class UserRegisterModalComponent extends Component<Props> {
  private reCaptchaRef = createRef<any>();

  executeReCaptcha = async () => {
    const { registerStore } = this.props;
    const { toggleReCaptcha, reCaptchaValid } = registerStore;

    if (reCaptchaValid) {
      toggleReCaptcha();
    } else {
      const response = await this.reCaptchaRef.current.executeAsync();
      if (response?.length > 0) {
        toggleReCaptcha();
        this.reCaptchaRef.current.reset();
      }
    }
  };

  render() {
    const { registerStore, loginStore, settings } = this.props;
    const {
      termsAndConditionsUrl,
      privacyPolicyUrl,
    } = settings?.themeSettings?.themeSettingsFields;
    const {
      closeRegisterModal,
      username,
      usernameInvalid,
      usernameErrorMessage,
      setUsername,
      validateUsername,
      email,
      emailInvalid,
      emailErrorMessage,
      setEmail,
      validateEmail,
      password,
      passwordInvalid,
      passwordErrorMessage,
      validatePassword,
      setPassword,
      passwordRepeat,
      passwordRepeatInvalid,
      passwordRepeatErrorMessage,
      validatePasswordRepeat,
      setPasswordRepeat,
      passwordsMatch,
      reCaptchaValid,
      tacAccepted,
      toggleTac,
      registerUser,
      registerUserMessage,
      registerUserMessageType,
      registerModalOpen,
    } = registerStore;
    const { openLoginModal } = loginStore;

    let registerUserMessageContainerClasses = [s.registerUserMessageContainer];
    let registerUserMessageClasses = [s.registerUserMessage];
    if (registerUserMessage !== "") {
      registerUserMessageContainerClasses.push(s.visible);
      if (registerUserMessageType === "error")
        registerUserMessageClasses.push(s.error);
    }

    return (
      <Modal
        title={tx("authentication.register")}
        open={registerModalOpen}
        onClose={closeRegisterModal}
      >
        <div className={s.titleWrapper}>
          <h3 className={s.title}>{tx("authentication.registerAnAccount")}</h3>
        </div>

        <div className={s.registerContainer}>
          <P medium className={s.registerText}>
            {tx("authentication.alreadyHaveAnAccount")}
          </P>

          <PseudoLink
            className={s.link}
            onClick={() => {
              closeRegisterModal();
              openLoginModal();
            }}
          >
            <P medium>{tx("authentication.loginHere")}</P>
          </PseudoLink>
        </div>
        <div className={s.contentContainer}>
          <div className={s.inputContainer}>
            <img
              src={Icons.user_small}
              alt="username icon"
              className={s.inputIcon}
            />
            <Input
              type="text"
              label={tx("input.username")}
              htmlFor="user-register-username"
              placeholder={tx("misc.startTyping")}
              showIcon
              value={username}
              onChange={setUsername}
              onBlur={validateUsername}
              error={usernameInvalid}
              errorMessage={usernameErrorMessage}
            />
          </div>
          <div className={s.inputContainer}>
            <img
              src={Icons.email_small}
              alt="email-icon"
              className={s.inputIcon}
            />
            <Input
              type="email"
              label={`${tx("input.email")}:`}
              htmlFor="user-register-email"
              placeholder={tx("misc.startTyping")}
              value={email}
              onChange={setEmail}
              onBlur={validateEmail}
              error={emailInvalid}
              errorMessage={emailErrorMessage}
            />
          </div>
          <div className={s.inputContainer}>
            <img
              src={Icons.lock_small}
              alt="lock-icon"
              className={s.inputIcon}
            />
            <Input
              type="password"
              label={`${tx("misc.password")}:`}
              htmlFor="user-register-password"
              placeholder={tx("misc.startTyping")}
              showIcon
              value={password}
              onChange={setPassword}
              onBlur={validatePassword}
              error={passwordInvalid}
              errorMessage={passwordErrorMessage}
            />
          </div>
          <div className={[s.inputContainer, s.noIcon].join(" ")}>
            <Input
              type="password"
              label={`${tx("misc.repeatPassword")}:`}
              htmlFor="user-register-password-repeat"
              placeholder={tx("misc.startTyping")}
              showIcon
              value={passwordRepeat}
              onChange={setPasswordRepeat}
              onBlur={validatePasswordRepeat}
              error={passwordRepeatInvalid}
              errorMessage={passwordRepeatErrorMessage}
            />
          </div>
          <div className={s.reCaptchaContainer}>
            <Checkbox
              type="check"
              onChange={this.executeReCaptcha}
              selected={reCaptchaValid}
            />
            <P className={s.reCaptchaText} small>
              {tx("authentication.imNotARobot")}
            </P>
            <img
              src={Icons.reCaptcha}
              alt="reCaptcha-icon"
              className={s.reCaptchaIcon}
            />
          </div>
          <ReCAPTCHA
            ref={this.reCaptchaRef}
            sitekey="6LfrtIEaAAAAAMEfdWwnrOG0SU77nvrfLisl1TNr"
            size="invisible"
          />
          <div className={s.tacContainer}>
            <Checkbox
              type="check"
              onChange={toggleTac}
              selected={tacAccepted}
            />
            <P className={s.tacText} small style={{ lineHeight: "28px" }}>
              {txWithComponent("settings.inputs.termsApprovalText", [
                <a
                  href={termsAndConditionsUrl}
                  target="_blank"
                  ref="noreferrer"
                  key={"terms1"}
                  className={s.tacLink}
                >
                  {tx("settings.inputs.termsAndConditions")}
                </a>,
              ])}
            </P>
          </div>
          <div className={registerUserMessageContainerClasses.join(" ")}>
            <P small className={registerUserMessageClasses.join(" ")}>
              {registerUserMessage}
            </P>
          </div>
          <div className={s.buttonContainer}>
            <Button
              size="large"
              color="red"
              className={s.button}
              disabled={
                emailInvalid ||
                passwordInvalid ||
                !passwordsMatch ||
                !reCaptchaValid ||
                !tacAccepted
              }
              onClick={registerUser}
            >
              {tx("authentication.register")}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
}

export const UserRegisterModal = (props: Props) => (
  <StaticQuery
    query={query}
    render={(data) => {
      return (
        <UserRegisterModalComponent {...props} settings={data?.siteSettings} />
      );
    }}
  />
);

const query = graphql`
  query {
    ...siteSettingsFragment
  }
`;
